import { getCookie, removeCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreYmmeDropdown, { YmmeDropdownStyles } from "@store/Components/Store/StoreYmmeDropdown";
import ClearYmmeState from "@store/Handlers/ClearYmmeState";
import FetchYmmeEngines from "@store/Handlers/FetchYmmeEngines";
import FetchYmmeMakes from "@store/Handlers/FetchYmmeMakes";
import FetchYmmeModels from "@store/Handlers/FetchYmmeModels";
import FetchYmmeYears from "@store/Handlers/FetchYmmeYears";
import SelectYmmeEngine from "@store/Handlers/SelectYmmeEngine";
import SelectYmmeMake from "@store/Handlers/SelectYmmeMake";
import SelectYmmeModel from "@store/Handlers/SelectYmmeModel";
import SelectYmmeYear from "@store/Handlers/SelectYmmeYear";
import ToggleYmmeActive from "@store/Handlers/ToggleYmmeActive";
import { CustomState } from "@store/Store/Reducers/YmmeReducer";
import { TYPOGRAPHY } from "@store/theme/design";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    return {
        makes: state.ymmeReducer.makes,
        models: state.ymmeReducer.models,
        years: state.ymmeReducer.years,
        engines: state.ymmeReducer.engines,
        ymmeActive: state.ymmeReducer.active,
        locationInfo: getLocation(state),
    };
};

const mapDispatchToProps = {
    FetchYmmeMakes,
    SelectYmmeMake,
    FetchYmmeModels,
    SelectYmmeModel,
    FetchYmmeYears,
    SelectYmmeYear,
    FetchYmmeEngines,
    SelectYmmeEngine,
    ClearYmmeState,
    ToggleYmmeActive,
};

const enum fields {
    textFieldValue = "textFieldValue",
}

interface OwnProps extends WidgetProps {
    FetchYmmeMakes: any;
    SelectYmmeMake: any;
    FetchYmmeModels: any;
    SelectYmmeModel: any;
    FetchYmmeYears: any;
    SelectYmmeYear: any;
    FetchYmmeEngines: any;
    SelectYmmeEngine: any;
    ClearYmmeState: any;
    ToggleYmmeActive: any;
    makes: any;
    models: any;
    years: any;
    engines: any;
    ymmeActive: any;
    fields: {
        [fields.textFieldValue]: string;
    };
    history: History;
    locationInfo: any;
}

export interface YmmeSearchStyles {
    widgetWrapper?: InjectableCss;
    ymmeItemsWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    modelDropdownStyles?: YmmeDropdownStyles;
    makeDropdownStyles?: YmmeDropdownStyles;
    yearDropdownStyles?: YmmeDropdownStyles;
    engineDropdownStyles?: YmmeDropdownStyles;
    searchButton?: ButtonPresentationProps;
    clearButton?: ButtonPresentationProps;
}

const commonGridItemStyles = () => {
    return css`
        align-items: center;

        select,
        button {
            border-radius: 2px;
        }

        select {
            max-height: 45px;
            border: none !important;
            box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.13);
            font-size: ${TYPOGRAPHY.size.small};
            padding-right: 45px !important;
            text-overflow: ellipsis;

            option {
                text-overflow: ellipsis;
            }
        }
    `;
};

const commonSelectContainerStyles = () => {
    return css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        display: block;
                        width: 100%;

                        + div {
                            margin-top: 10px;
                        }
                    `,
                    null,
                    css`
                        display: inline-flex;
                        flex-direction: column;
                        width: 100%;
                        margin-right: 20px;

                        + div {
                            margin-top: 0;
                        }
                    `,
                ],
                "min",
            )}
    `;
};

export const ymmeSearchStyles: YmmeSearchStyles = {
    searchButton: {
        icon: {
            src: "Search",
            position: "right",
        },
        color: "secondary",
        css: css`
            height: 45px;
            width: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100% !important;
                            margin-top: 15px;
                        `,
                        null,
                        css`
                            width: auto !important;
                            padding: 0 15px;
                            margin-top: 0;
                            span {
                                margin: 0;
                            }
                            > span:first-child {
                                display: none;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    clearButton: {
        variant: "primary",
        css: css`
            height: 45px;
            width: auto;
            background: transparent !important;
            border: none !important;
            text-decoration: underline;
            span {
                white-space: nowrap;
            }
            &:hover {
                text-decoration: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100% !important;
                        `,
                        null,
                        css`
                            margin-left: 15px;
                            width: auto !important;
                            padding: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    widgetWrapper: {
        css: css`
            > div {
                align-items: center;
            }
        `,
    },
    ymmeItemsWrapper: {
        css: css`
            ${commonGridItemStyles()}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: block;
                            padding-top: 0;
                            padding-bottom: 0;
                        `,
                        null,
                        css`
                            display: flex;
                            padding: 10px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    makeDropdownStyles: {
        container: {
            css: commonSelectContainerStyles(),
        },
    },
    modelDropdownStyles: {
        container: {
            css: commonSelectContainerStyles(),
        },
    },
    yearDropdownStyles: {
        container: {
            css: commonSelectContainerStyles(),
        },
    },
    engineDropdownStyles: {
        container: {
            css: commonSelectContainerStyles(),
        },
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
            display: inline-block;
            width: auto;
            white-space: nowrap;
            margin-right: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 1em 0;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

type Props = HasHistory & OwnProps;

const YmmeSearch: React.FC<Props> = ({
    FetchYmmeMakes,
    SelectYmmeMake,
    FetchYmmeModels,
    SelectYmmeModel,
    FetchYmmeYears,
    SelectYmmeYear,
    FetchYmmeEngines,
    SelectYmmeEngine,
    ClearYmmeState,
    ToggleYmmeActive,
    makes,
    models,
    years,
    engines,
    ymmeActive,
    fields,
    history,
    locationInfo,
}) => {
    useEffect(() => {
        const makesSelection = getCookie("ymmContextMakeKey") || makes.selection;
        const modelsSelection = getCookie("ymmContextModelKey") || models.selection;
        const yearsSelection = getCookie("ymmContextYearKey") || years.selection;
        const enginesSelection = getCookie("ymmContextEngineKey") || engines.selection;

        // todo: See with backend to return a proper bool
        if (ymmeActive !== true && getCookie("ymmsearch") === "true") {
            ToggleYmmeActive({ active: true });
        }

        FetchYmmeMakes(false);

        if (makesSelection !== null && makesSelection !== "") {
            SelectYmmeMake({ id: makesSelection });
            FetchYmmeModels({ make: makesSelection });
        }

        if (modelsSelection !== null && modelsSelection !== "") {
            SelectYmmeModel({ id: modelsSelection });
        }

        if (yearsSelection !== null && yearsSelection !== "") {
            SelectYmmeYear({ id: yearsSelection });
        }

        if (enginesSelection !== null && enginesSelection !== "") {
            SelectYmmeEngine({ id: enginesSelection });
        }

        // Last two dropdowns are independents
        if (years.selection === "") {
            FetchYmmeYears({ make: makesSelection, model: modelsSelection, engine: enginesSelection });
        }

        if (engines.selection === "") {
            FetchYmmeEngines({ make: makesSelection, model: modelsSelection, year: yearsSelection });
        }
    }, []);

    const tradMake = translate("Make");
    const tradModel = translate("Model");
    const tradYear = translate("Year");
    const tradEngine = translate("Engine");

    const handleMakeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        SelectYmmeMake({ id: event.currentTarget.value });
        FetchYmmeModels({ make: event.currentTarget.value });
    };
    const handleModelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        SelectYmmeModel({ id: event.currentTarget.value });
        FetchYmmeYears({ make: makes.selection, model: event.currentTarget.value, engine: "" });
        FetchYmmeEngines({ make: makes.selection, model: event.currentTarget.value, year: "" });
    };
    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        SelectYmmeYear({ id: event.currentTarget.value });
        FetchYmmeEngines({ make: makes.selection, model: models.selection, year: event.currentTarget.value });
    };
    const handleEngineChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        SelectYmmeEngine({ id: event.currentTarget.value });
        FetchYmmeYears({ make: makes.selection, model: models.selection, engine: event.currentTarget.value });
    };
    const triggerYmmeSearch = () => {
        setCookie("ymmContextMakeKey", makes.selection);
        setCookie("ymmContextModelKey", models.selection);
        setCookie("ymmContextYearKey", years.selection);
        setCookie("ymmContextEngineKey", engines.selection);
        setCookie("ymmsearch", "true");
        ToggleYmmeActive({ active: true });
        if (
            locationInfo.pathname.toLowerCase().endsWith("/catalog/products/") ||
            locationInfo.pathname.toLowerCase().endsWith("/catalog/products")
        ) {
            global.location.reload();
        } else {
            // history.push("/Catalog/Products"); // Does not play well with cookies
            global.location.replace(translate("/Catalog/Products"));
        }
    };
    const triggerYmmeClear = () => {
        removeCookie("ymmContextMakeKey");
        removeCookie("ymmContextModelKey");
        removeCookie("ymmContextYearKey");
        removeCookie("ymmContextEngineKey");
        setCookie("ymmsearch", "false");
        ClearYmmeState(false);
        ToggleYmmeActive({ active: false });
        global.location.replace("/");
    };

    return (
        <>
            <StyledWrapper {...ymmeSearchStyles.widgetWrapper}>
                <StyledWrapper {...ymmeSearchStyles.ymmeItemsWrapper}>
                    <StyledWrapper {...ymmeSearchStyles.titleWrapper}>{fields.textFieldValue}</StyledWrapper>
                    <StoreYmmeDropdown
                        active={ymmeActive}
                        dropdownData={makes}
                        onValueChange={handleMakeChange}
                        extendedStyles={ymmeSearchStyles.makeDropdownStyles}
                        selectLabel={tradMake}
                    />
                    <StoreYmmeDropdown
                        active={ymmeActive}
                        dropdownData={models}
                        onValueChange={handleModelChange}
                        extendedStyles={ymmeSearchStyles.modelDropdownStyles}
                        selectLabel={tradModel}
                    />
                    <StoreYmmeDropdown
                        active={ymmeActive}
                        dropdownData={years}
                        onValueChange={handleYearChange}
                        extendedStyles={ymmeSearchStyles.yearDropdownStyles}
                        selectLabel={tradYear}
                    />
                    <StoreYmmeDropdown
                        active={ymmeActive}
                        dropdownData={engines}
                        onValueChange={handleEngineChange}
                        extendedStyles={ymmeSearchStyles.engineDropdownStyles}
                        selectLabel={tradEngine}
                    />
                    {makes.selection !== "" && models.selection !== "" ? (
                        <Button {...ymmeSearchStyles.searchButton} onClick={triggerYmmeSearch}>
                            {translate("Find Part")}
                        </Button>
                    ) : (
                        <Button {...ymmeSearchStyles.searchButton} disabled>
                            {translate("Find Part")}
                        </Button>
                    )}
                    <Button {...ymmeSearchStyles.clearButton} onClick={triggerYmmeClear}>
                        {translate("Clear")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Header",
    fieldDefinitions: [
        {
            name: fields.textFieldValue,
            displayName: "Text",
            editorTemplate: "TextField",
            defaultValue: "Search for bus parts",
            fieldType: "Translatable",
            tooltip: "Title that will be displayed next to the YMME selector",
            placeholder: "Widget title",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(YmmeSearch)),
    definition,
};

export default widgetModule;
